import React from 'react';
import Editor from '@monaco-editor/react';
// No static `monaco-editor` import — the barrel would re-pull the TS language
// service trimmed from the bundle; everything reads the live instance.
import { useThemeStore } from '../../store/themeStore';
import { registerMonacoThemes, resolveMonacoTheme } from '../../utils/monacoThemes';
import { remeasureMonacoFontsWhenReady } from '../../utils/monacoFonts';
import { registerHexLanguage } from '../../utils/monacoHexLanguage';

/**
 * The Edit cell modal's editor: Monaco with syntax highlighting for the
 * value's display mode (owner, 30.09.2026: a 9 348-character XML value in a
 * plain textarea).
 *
 * Deliberately NOT ui/CodeEditor: that one decorates `{{variables}}` and
 * offers environment completions (a DB value containing `{{x}}` would look
 * like an env var), follows the global word-wrap preference and has its own
 * toolbar. Monaco's find widget handles Ctrl+F in here — the grid's find stays
 * out of it (dbGridKeyGuard: no grid keys while a modal is open).
 */
const LANGUAGE = { text: 'plaintext', json: 'json', xml: 'xml', binary: 'apiboo-hex' };

function useIsDark() {
  const theme = useThemeStore((s) => s.theme);
  return (
    theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  );
}

export function cellEditorLanguage(mode) {
  return LANGUAGE[mode] || 'plaintext';
}

export default function DbCellCodeEditor({ value, onChange, mode, wordWrap, readOnly }) {
  const isDark = useIsDark();
  return (
    <Editor
      value={value}
      language={cellEditorLanguage(mode)}
      theme={resolveMonacoTheme(isDark)}
      onChange={(v) => onChange?.(v ?? '')}
      beforeMount={(monaco) => {
        registerMonacoThemes(monaco);
        registerHexLanguage(monaco);
        // A cell value is data, not a document being authored: no red
        // squiggles for a JSON fragment or an XML snippet without a root.
        monaco.languages.json?.jsonDefaults?.setDiagnosticsOptions({ validate: false });
      }}
      onMount={(editor, monaco) => {
        remeasureMonacoFontsWhenReady(monaco);
        if (!readOnly) editor.focus();
      }}
      options={{
        readOnly,
        wordWrap: wordWrap ? 'on' : 'off',
        minimap: { enabled: false },
        automaticLayout: true,
        scrollBeyondLastLine: false,
        fontSize: 12,
        lineNumbersMinChars: 3,
        folding: true,
        renderLineHighlight: readOnly ? 'none' : 'line',
        tabSize: 2,
        padding: { top: 8, bottom: 8 },
      }}
    />
  );
}
